import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import { AppEnvironment, appEnvironment } from '../../services/environment';

const TONE: Record<Exclude<AppEnvironment, 'production'>, { label: string; hint: string; className: string; dot: string }> = {
    integration: {
        label: 'ENVIRONMENT.INTEGRATION',
        hint: 'ENVIRONMENT.INTEGRATION_HINT',
        className: 'border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-300',
        dot: 'bg-amber-500',
    },
    local: {
        label: 'ENVIRONMENT.LOCAL',
        hint: 'ENVIRONMENT.LOCAL_HINT',
        className: 'border-blue-500/40 bg-blue-500/10 text-blue-800 dark:text-blue-300',
        dot: 'bg-blue-500',
    },
};

/**
 * "INT" or "Local", in the colour of the favicon's dot, wherever this is not
 * production. Nothing at all on prod: customers never see it.
 */
export function EnvironmentBadge({ className, env = appEnvironment() }: { className?: string; env?: AppEnvironment }) {
    const { t } = useTranslation();
    if (env === 'production') return null;
    const tone = TONE[env];
    const hint = t(tone.hint) as string;
    return (
        <span
            role="status"
            title={hint}
            aria-label={hint}
            data-testid="environment-badge"
            data-env={env}
            className={cn(
                'inline-flex h-6 shrink-0 select-none items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium',
                tone.className,
                className,
            )}
        >
            <span className={cn('size-1.5 rounded-full', tone.dot)} aria-hidden />
            {t(tone.label)}
        </span>
    );
}
